import defaultsDeep from 'lodash.defaultsdeep';

import styleParamsToSettings from './styleParamsToSettings/styleParamsToSettings';
import migrateSettings from '@wix/podcasts-common/dist/settings/migrateSettings';

import getDefaultSettings from './getDefaultSettings';

import getSettingsFontsByTheme from './styleParamsToSettings/getSettingsFontsByTheme';
import getSettingsColorsByTheme from './styleParamsToSettings/getSettingsColorsByTheme';

import prepareSettingsDefaults from './prepareSettingsDefaults';

import { DEFAULT_SETTING } from '../constants';

import { ISettings, IStyleParamsClient } from '../types';
import { ILogger } from '../../../types/types';
import { TPADeviceType } from '@wix/podcasts-common/dist/tpa';

interface ISettingsClientOptions {
  logger: ILogger;
}

class SettingsClient {
  private _styleParamsClient: IStyleParamsClient;
  private _logger: ILogger;

  constructor(
    styleParamsClient: IStyleParamsClient,
    options: ISettingsClientOptions,
  ) {
    this._styleParamsClient = styleParamsClient;
    this._logger = options.logger;
  }

  async getSettings(deviceType: TPADeviceType): Promise<ISettings> {
    this._logger.log('SettingsClient: settings load started');

    const { styleParams, themeColors, themeFonts } =
      await this._styleParamsClient.get();

    const defaultSettings = {
      ...DEFAULT_SETTING,
      fonts: getSettingsFontsByTheme(themeFonts),
      colors: getSettingsColorsByTheme(themeColors),
    };

    let settings = styleParamsToSettings(styleParams, deviceType);

    if (settings) {
      settings = migrateSettings(settings);

      prepareSettingsDefaults(settings);

      settings = defaultsDeep(settings, defaultSettings);

      this._logger.log('SettingsClient: settings load completed');

      return settings;
    }

    this._logger.warn(
      'SettingsClient: settings is empty, falling back to default settings.',
    );

    return defaultSettings;
  }

  getDefaultSettings() {
    return getDefaultSettings();
  }

  /**
   * Subscribe on settings changed
   * @returns - unsubscribe function
   */
  subscribe(
    fn: (settings: ISettings) => void,
    deviceType: TPADeviceType,
  ): Function {
    return this._styleParamsClient.subscribe(async () => {
      fn(await this.getSettings(deviceType));
    });
  }
}

export default SettingsClient;
